"use client";

import React from "react";
import { Syringe, Pill, Activity, Sparkles, Check } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const treatments = [{
  id: "mesoterapia",
  title: "Mesoterapia capilar",
  description: "Tratamiento local mínimamente invasivo que infiltra principios activos directamente en el folículo piloso.",
  iconName: "Syringe",
  isComplex: true
}, {
  id: "medico",
  title: "Tratamiento médico",
  description: "Abordaje farmacológico oral y tópico bajo estricta supervisión médica. Utilizamos las moléculas más eficaces y seguras para estabilizar la alopecia y mejorar la densidad capilar desde el interior, ajustando las dosis a las necesidades específicas de cada paciente.",
  iconName: "Pill",
  isComplex: false
}, {
  id: "cirugia",
  title: "Cirugía capilar",
  description: "Técnicas avanzadas de microinjerto capilar para restaurar zonas con pérdida irreversible. Un procedimiento minucioso y artístico que garantiza resultados naturales, densos y definitivos, respetando la fisonomía de cada paciente.",
  iconName: "Activity",
  isComplex: false
}, {
  id: "femenina",
  title: "Alopecia femenina",
  description: "Enfoque especializado y empático para la pérdida de cabello en mujeres. Comprendemos las particularidades hormonales y genéticas femeninas, ofreciendo protocolos diagnósticos y terapéuticos diseñados exclusivamente para restaurar su salud capilar y bienestar emocional.",
  iconName: "Sparkles",
  isComplex: false
}];
const mesotherapyItems = [{
  id: 1,
  name: "Medicamentos",
  desc: "Minoxidil y Dutasteride para frenar la caída y estimular el crecimiento."
}, {
  id: 2,
  name: "Plasma Rico en Plaquetas (PRP)",
  desc: "Factores de crecimiento de tu propia sangre para regenerar el tejido."
}, {
  id: 3,
  name: "Vitaminas y complejos revitalizantes",
  desc: "Nutrición intensiva para revitalizar el cabello debilitado."
}, {
  id: 4,
  name: "Polinucleótidos",
  desc: "Bioestimulación avanzada para mejorar la calidad celular del cuero cabelludo."
}];
const iconMap: Record<string, React.ElementType> = {
  Syringe,
  Pill,
  Activity,
  Sparkles
};
export default function ClinicalTreatments() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="clinicaltreatments" className="bg-background">
      <div className="bg-secondary text-secondary-foreground py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {}
          <div ref={headerReveal.ref} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <span className="text-primary tracking-wider uppercase text-sm mb-4 block !font-bold">
              Tratamientos Clínicos
            </span>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-secondary-foreground">Soluciones para cada necesidad capilar</h2>
            <p className="text-lg md:text-xl text-secondary-foreground/80 font-light">
              Recupera densidad, confianza y bienestar con un enfoque médico personalizado.
            </p>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {treatments.map((treatment, i) => {
            const Icon = iconMap[treatment.iconName];
            const cardReveal = useRevealOnScroll({
              threshold: 0.1
            });
            return <div key={treatment.id} data-index={i} ref={cardReveal.ref} className={`transition-all duration-1000 delay-[${i * 150}ms] ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                  <Card className="h-full rounded-md shadow-lg bg-card text-card-foreground border-none overflow-hidden flex flex-col">
                    <CardHeader className="pb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-md flex items-center justify-center mb-4">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="font-default text-2xl text-foreground">
                        {treatment.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col">
                      <p className="text-muted-foreground leading-relaxed mb-6">
                        {treatment.description}
                      </p>

                      {treatment.isComplex && <div className="mt-auto pt-4">
                          <Separator className="mb-6" />
                          <ul className="space-y-5">
                            {mesotherapyItems.map((item, j) => <li key={item.id} data-index={j} className="flex items-start gap-4">
                                <div className="mt-0.5 bg-primary/10 p-1.5 rounded-md shrink-0">
                                  <Check className="w-4 h-4 text-primary" />
                                </div>
                                <div>
                                  <h4 className="font-default font-semibold text-foreground text-sm md:text-base">
                                    {item.name}
                                  </h4>
                                  <p className="text-sm text-muted-foreground mt-1">
                                    {item.desc}
                                  </p>
                                </div>
                              </li>)}
                          </ul>
                        </div>}
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}